import React, { forwardRef, useMemo } from "react";
import MobilityMap, { mapTypes } from "components/MobilityMap";
import { jsonFetch } from "lib/fetcher";

import {
  urls as choroplethUrls,
  useCountryChoropleth,
  useCountyChoropleth,
  useStateChoropleth,
  useWorldChoropleth,
} from "lib/api/distancing/choropleth";

import { useCountryCenter, useStateCenter, useCountyCenter } from "lib/config";

import { urls as hexagonsUrl } from "lib/api/distancing/hexagons";

import useSWR from "swr";

import bboxPolygon from "@turf/bbox";

function getBoundingBox(geoJson) {
  if (!geoJson) {
    return null;
  }

  return bboxPolygon(geoJson);
}


function useMapPosition(level, country, state, _county, geoJson) {

  const countryCenter = useCountryCenter(country);
  // const stateCenter = useStateCenter(country, state);


  const bbox = useMemo(() => {  
    if (level !== "county" && level !== "state") {
      return null
    }
    return getBoundingBox(geoJson);
  }, [level, geoJson]);

  if (level === "world") {
    return {
      initialZoom: 2,
      initialLatitude: -30.0959846,
      initialLongitude: -50.8617484,
    }
  } 
  
  if (level === "country") {
    return {
      initialZoom: countryCenter.zoom,
      initialLatitude: countryCenter.center[1],
      initialLongitude: countryCenter.center[0],
    };
  } 

  return { initialBbox: bbox };
}

function DistancingMobilityMap(
  {
    level,
    mapType,
    country,
    state,
    county,
    selectedDate,
    baselineDate,
    onPolygonClick,
    hexResolution,
    onBoundsChange,
    searchPlaces,
  },
  ref
) {
  const worldGeoJson = useSWR(
    mapType === mapTypes.worldChoropleth
      ? choroplethUrls.getWorldGeoJson()
      : null,
    jsonFetch,
    { suspense: false }
  );

  const countryGeoJson = useSWR(
    mapType === mapTypes.countryChoropleth
      ? choroplethUrls.getCountryGeoJson(country)
      : null,
    jsonFetch,
    { suspense: false }
  );

  const stateGeoJson = useSWR(
    mapType === mapTypes.stateChoropleth
      ? choroplethUrls.getStateGeoJson(country, state)
      : null,
    jsonFetch,
    { suspense: false }
  );

  // console.log(`Map Type: ${mapType == mapTypes.countyChoropleth} <=> mapType: ${mapType} = ${mapTypes.countyChoropleth}`)
  const countyGeoJson = useSWR(
    mapType === mapTypes.countyChoropleth
      ? choroplethUrls.getCountyGeoJson(country, state, county)
      : null,
    jsonFetch,
    { suspense: false }
  );

  const worldChoropleth = useWorldChoropleth(
    mapType === mapTypes.worldChoropleth,
    selectedDate,
    baselineDate
  );

  const countryChoropleth = useCountryChoropleth(
    mapType === mapTypes.countryChoropleth,
    country,
    selectedDate,
    baselineDate
  );

  const stateChoropleth = useStateChoropleth(
    mapType === mapTypes.stateChoropleth,
    country,
    state,
    selectedDate,
    baselineDate
  );

  const countyChoropleth = useCountyChoropleth(
    mapType === mapTypes.countyChoropleth,
    country,
    state,
    county,
    selectedDate,
    baselineDate
  );

  // (country, date, level1Id, resolution) {
  const stateHexagons = useSWR(
    mapType === mapTypes.stateHexagons
      ? hexagonsUrl.getStateHexagons(
          country,
          selectedDate,
          state,
          hexResolution
        )
      : null,
    jsonFetch,
    { suspense: false }
  );

  const geoJson =
    worldGeoJson.data ||
    countryGeoJson.data ||
    stateGeoJson.data ||
    countyGeoJson.data;


  const mapPosition = useMapPosition(level, country, state, county, geoJson);

  return (
    <MobilityMap
      ref={ref}
      searchPlaces={searchPlaces}
      type={mapType}
      data={
        worldChoropleth.data ||
        countryChoropleth.data ||
        stateChoropleth.data ||
        countyChoropleth.data ||
        stateHexagons.data
      }
      geoJson={geoJson}
      onPolygonClick={onPolygonClick}
      onBoundsChange={onBoundsChange}
      {...mapPosition}
    />
  );
}

export default forwardRef(DistancingMobilityMap);

if (process.env.NODE_ENV === "development") {
  DistancingMobilityMap.whyDidYouRender = {
    logOnDifferentValues: true,
  };
}
